// Top-up amounts and their bonuses — "add ₹500, get ₹50 extra" (spec/06 §5, F-WAL-03; spec/12 §7.1, F-KIO-10).
//
// **One tier rule, defined once here.** A top-up of `amount` earns the bonus of the best `wallet.topupOffers` tier it
// reaches: among the tiers whose `minTopupMinor` the amount meets and whose bonus is above zero, the largest bonus
// (a tie goes to the higher threshold, then to the tier listed first). The server applies it when a kiosk top-up is
// asked for (`wallet/kiosk-topup.ts` names the tier this picks, and the ledger credits exactly its bonus to
// `wallet_bonus`), the station shop draws each amount's badge with it (`passes/station.ts`), and the kiosk shows the
// bonus an "Other amount" earns with it before the player confirms — so the badge a player sees is the bonus credited.
//
// The amounts the kiosk offers (`topUpPresetAmounts`): the venue's own list (`wallet.topupPresets`, venue over
// organisation) when it has one, each within the per-top-up minimum and maximum; with none set — the default — the
// amounts the kiosk has always shown: the smallest top-up and each tier's threshold, then two, five and ten times the
// smallest while there are fewer than four. Cheapest first, no duplicates.
//
// Integer minor units throughout, no currency (the caller's amounts share one), no clock. **Light on purpose**: the
// kiosk's lazily loaded Top up chunk imports this as its own entry, `@clavian/domain/topup-offers`, so it imports
// nothing at runtime — not even `@clavian/contracts`, whose barrel carries Zod (and `eval`, which a console's CSP refuses).
/** How many amounts the kiosk shows at most (`wallet.topupPresets` takes up to this many). */
export const MAX_TOPUP_PRESETS = 12;
const isAmount = (value) => Number.isSafeInteger(value) && value > 0;
/**
 * The tier a top-up of `amountMinor` earns — the largest bonus among the tiers it reaches (a tie: the higher threshold,
 * then the one listed first) — or `null` when it reaches none, or none gives a bonus. **The** rule: the server credits
 * this tier's bonus, and every screen that shows a bonus reads it from here.
 */
export function bestTopUpTier(tiers, amountMinor) {
    let best = null;
    for (const tier of tiers) {
        if (!(tier.bonusMinor > 0) || !Number.isSafeInteger(tier.bonusMinor))
            continue;
        if (!Number.isSafeInteger(tier.minTopupMinor) || amountMinor < tier.minTopupMinor)
            continue;
        if (best === null ||
            tier.bonusMinor > best.bonusMinor ||
            (tier.bonusMinor === best.bonusMinor && tier.minTopupMinor > best.minTopupMinor)) {
            best = tier;
        }
    }
    return best;
}
/** The bonus a top-up of `amountMinor` earns, in minor units: `bestTopUpTier`'s, or 0. */
export function topUpBonusMinor(tiers, amountMinor) {
    return bestTopUpTier(tiers, amountMinor)?.bonusMinor ?? 0;
}
/** The amounts the kiosk has always derived: the smallest top-up and each threshold, then 2×, 5×, 10× the smallest. */
function derivedAmounts(range, tiers) {
    const { minMinor: min, maxMinor: max } = range;
    const amounts = new Set();
    if (min > 0 && min <= max)
        amounts.add(min);
    for (const tier of tiers)
        if (tier.minTopupMinor >= min && tier.minTopupMinor <= max)
            amounts.add(tier.minTopupMinor);
    for (const factor of [2, 5, 10]) {
        if (amounts.size >= 4)
            break;
        const amount = min * factor;
        if (min > 0 && amount <= max)
            amounts.add(amount);
    }
    return amounts;
}
/**
 * The amounts the kiosk offers, cheapest first: the venue's presets within the range when it has set any that fit,
 * else the derived default (what the kiosk showed before `wallet.topupPresets` existed). A preset outside the minimum
 * and maximum is left out, never clamped — the console says so before it is saved.
 */
export function topUpPresetAmounts(range, presets, tiers) {
    const chosen = new Set(presets.filter((amount) => isAmount(amount) && amount >= range.minMinor && amount <= range.maxMinor));
    const amounts = chosen.size > 0 ? chosen : derivedAmounts(range, tiers);
    return [...amounts].sort((a, b) => a - b).slice(0, MAX_TOPUP_PRESETS);
}
/** The kiosk's top-up tiles: each amount with the tier it earns (`bestTopUpTier`), cheapest first. */
export function topUpTiles(range, presets, tiers) {
    return topUpPresetAmounts(range, presets, tiers).map((amountMinor) => ({ amountMinor, tier: bestTopUpTier(tiers, amountMinor) }));
}
/**
 * The tile worth highlighting as best value — the most bonus per rupee added (a tie: the larger amount) — or `null`
 * when fewer than two tiles earn a bonus (one bonus tile needs no "best"). Index into `tiles`.
 */
export function bestValueTileIndex(tiles) {
    let best = null;
    let earning = 0;
    tiles.forEach((tile, index) => {
        if (!(tile.bonusMinor > 0) || !(tile.amountMinor > 0))
            return;
        earning += 1;
        if (best === null) {
            best = index;
            return;
        }
        const current = tiles[best];
        // Compare bonus/amount without a float: b1/a1 > b2/a2 ⇔ b1·a2 > b2·a1.
        const lhs = tile.bonusMinor * current.amountMinor;
        const rhs = current.bonusMinor * tile.amountMinor;
        if (lhs > rhs || (lhs === rhs && tile.amountMinor > current.amountMinor))
            best = index;
    });
    return earning >= 2 ? best : null;
}
/**
 * Whether an amount — a tile, or one the player typed — can be topped up, in the order the server checks
 * (`WalletService.checkTopUp`): a whole positive amount, at least the minimum, at most the maximum, and not taking the
 * balance past its ceiling with the bonus it earns. With the bonus when it can.
 */
export function checkTopUpAmount(input) {
    const { amountMinor } = input;
    if (!isAmount(amountMinor))
        return { ok: false, problem: 'invalid' };
    if (amountMinor < input.minMinor)
        return { ok: false, problem: 'below-minimum' };
    if (amountMinor > input.maxMinor)
        return { ok: false, problem: 'above-maximum' };
    const tier = bestTopUpTier(input.tiers, amountMinor);
    const bonusMinor = tier?.bonusMinor ?? 0;
    if (input.balanceMinor + amountMinor + bonusMinor > input.maxBalanceMinor)
        return { ok: false, problem: 'over-balance' };
    return { ok: true, bonusMinor, tier };
}
//# sourceMappingURL=topup-offers.js.map